import { CommonModule, CurrencyPipe } from "@angular/common";
import { HttpClient } from "@angular/common/http";
import { NgModule } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { RouterModule } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import {
  ActivityIndicatorComponent,
  ChartComponent,
  ListSkeletonComponent,
  LoadingTextSkeletonDirective,
  PageContentComponent,
  TableContainerComponent,
  TableSkeletonComponent,
  TableStripedHorizontalDirective,
  TilesSkeletonComponent
} from "./components";
import { AlertDialogComponent } from "./components/alert-dialog/alert-dialog.component";
import { CarWorkshopPendeciesModalComponent } from "./components/car-workshop-pendecies-modal/car-workshop-pendecies-modal.component";
import { ConfirmReasonModalComponent } from "./components/confirm-reason-modal/confirm-reason-modal.component";
import { DownloadUploadFileModule } from "./components/download-upload-file/download-upload-file.module";
import { DynamicLoadingDrivebComponent } from "./components/dynamic-loading-driveb/dynamic-loading-driveb.component";
import { EmailModalComponent } from "./components/email-modal/email-modal.component";
import { KanbanColumnComponent } from "./components/kanban/kanban-column/kanban-column.component";
import { KanbanComponent } from "./components/kanban/kanban.component";
import { MandatoryDataUpdateCarWorkshopDialogComponent } from "./components/mandatory-data-update-car-workshop-dialog/mandatory-data-update-car-workshop-dialog.component";
import { DetailViewComponent } from "./components/master-detail/detail/detail-view.component";
import { MasterDetailViewComponent } from "./components/master-detail/master-detail-view.component";
import { MasterViewComponent } from "./components/master-detail/master/master-view.component";
import { MasterWrapperComponent } from "./components/master-wrapper/master-wrapper.component";
import { GenericAlertModalComponent } from "./components/modal-alerts/generic-alert-modal.component";
import { ModalApprovalModule } from "./components/modal-approval/modal-approval.module";
import { NotFoundRouteComponent } from "./components/not-found-route/not-found-route.component";
import { PageHeadingComponent } from "./components/page-heading/page-heading.component";
import { PaginatorComponent } from "./components/paginator/paginator.component";
import { ReasonDialogComponent } from "./components/reason-dialog/reason-dialog.component";
import { RescheduleModalModule } from "./components/reschedule-modal/reschedule-modal.module";
import { ResizeHandleComponent } from "./components/resize-handle";
import { ThreadComponent } from "./components/thread/thread.component";
import { OnClickGaDirective } from "./directives/on-click-ga.directive";
import { AngularMaterialModule } from "./modules/angular-material/angular-material.module";
import {
  AddCountryCodePipe,
  CountryDatePipe,
  CurrencyFormatPipe,
  MileageUnitLengthPipe,
  NoValuePipe
} from "./pipes";
import { BytesPipe } from "./pipes/bytes-pipe.pipe";
import { ChecklistPipe } from "./pipes/checklist.pipe";
import { CnpjCuitPipe } from "./pipes/cnpj-cuit.pipe";
import { DecimalToPercentagePipe } from "./pipes/decimal-to-percentage.pipe";
import { NullishToZeroPipe } from "./pipes/nullish-to-zero.pipe";
import { NumberOfActivesPipe } from "./pipes/number-of-actives.pipe";
import { RemoveSymbolsPipe } from "./pipes/remove-symbols/remove-symbols.pipe";
import { WidgetModule } from "./widgets/widget.module";
import { CreatePartDerivationModalComponent } from "./components/create-part-derivation-modal/create-part-derivation-modal.component";
import { TranslationNamePipe } from "./pipes/translation-name.pipe";
import { DownloadDataApiExcelComponent } from "./components/download-data-api-excel/download-data-api-excel.component";
import {
  BciComponentModule,
  BciLayoutModule,
  BciSharedModule
} from "libs/core/src";
import { IConfig, provideNgxMask } from "ngx-mask";
import { ConfirmModalComponent } from "./components/generic-confirm-modal/confirm-modal/confirm-modal.component";
import { WarningModalComponent } from "./components/generic-warning-modal/warning-modal.component";
import { UploadTemplateModalComponent } from "./components/ulpoad-template-modal/upload-template-modal/upload-template-modal.component";
import { UploadErrorsTemplateModalComponent } from "./components/ulpoad-template-modal/upload-errors-template-modal/upload-errors-template-modal.component";
import { CustomFilterMultiSelectModalComponent } from "./components/custom-filter-multi-select-modal/custom-filter-multi-select-modal.component";
import { SimpleCardStatusComponent } from "./components/simple-card-status/simple-card-status.component";
import { CustomImportMultiSelectModalComponent } from "./components/custom-import-multi-select-modal/custom-import-multi-select-modal.component";
import { ModalConfirmVehicleArrivalComponent } from "./components/modal-confirm-vehicle-arrival/modal-confirm-vehicle-arrival.component";
import { ModalEditWorkshopPrevisionDateComponent } from "./components/modal-edit-workshop-prevision-date/modal-edit-workshop-prevision-date.component";
import { NgxMaskDirective } from "ngx-mask";
import { GenericMultiSelectAutocompleteComponent } from "./components/generic-multi-select-autocomplete/generic-multi-select-autocomplete.component";
import { PlatformNewsModalComponent } from "./components/platform-news-modal/platform-news-modal.component";
import { TagChipComponent } from "./components/tag-chip/tag-chip.component";
import { TagListComponent } from "./components/tag-list/tag-list.component";
import { TagPopoverComponent } from "./components/tag-popover/tag-popover.component";

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, "/assets/i18n/", ".json");
}

const maskConfig: Partial<IConfig> = {
  validation: false
};

const components = [
  ActivityIndicatorComponent,
  PaginatorComponent,
  ConfirmReasonModalComponent,
  MandatoryDataUpdateCarWorkshopDialogComponent,
  ChartComponent,
  SimpleCardStatusComponent,
  EmailModalComponent,
  CarWorkshopPendeciesModalComponent,
  ReasonDialogComponent,
  DynamicLoadingDrivebComponent,
  MasterWrapperComponent,
  MasterViewComponent,
  DetailViewComponent,
  ResizeHandleComponent,
  MasterDetailViewComponent,
  PageContentComponent,
  PageHeadingComponent,
  NotFoundRouteComponent,
  ThreadComponent,
  AlertDialogComponent,
  ListSkeletonComponent,
  TableSkeletonComponent,
  TilesSkeletonComponent,
  TableContainerComponent,
  KanbanComponent,
  KanbanColumnComponent,
  CreatePartDerivationModalComponent,
  DownloadDataApiExcelComponent,
  ConfirmModalComponent,
  WarningModalComponent,
  UploadTemplateModalComponent,
  UploadErrorsTemplateModalComponent,
  CustomFilterMultiSelectModalComponent,
  CustomImportMultiSelectModalComponent,
  PlatformNewsModalComponent,
  ModalConfirmVehicleArrivalComponent,
  ModalEditWorkshopPrevisionDateComponent,
  GenericMultiSelectAutocompleteComponent,
  TagChipComponent,
  TagListComponent,
  TagPopoverComponent
];

const pipes = [
  BytesPipe,
  NullishToZeroPipe,
  CnpjCuitPipe,
  ChecklistPipe,
  DecimalToPercentagePipe,
  NumberOfActivesPipe,
  DecimalToPercentagePipe,
  MileageUnitLengthPipe,
  CurrencyFormatPipe,
  CountryDatePipe,
  RemoveSymbolsPipe,
  NoValuePipe,
  AddCountryCodePipe,
  TranslationNamePipe
];

const directives = [
  OnClickGaDirective,
  LoadingTextSkeletonDirective,
  TableStripedHorizontalDirective
];

@NgModule({
  declarations: [
    ...components,
    ...pipes,
    ...directives,
    GenericAlertModalComponent
  ],
  imports: [
    /*Angular IO*/
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    /**Angular Material*/
    AngularMaterialModule,
    /** Text Mask */
    WidgetModule,
    RescheduleModalModule,
    ModalApprovalModule,
    TranslateModule,
    DownloadUploadFileModule,
    NgxMaskDirective,
    /**Bosch BCI Web*/
    BciComponentModule,
    BciLayoutModule
  ],
  exports: [
    /**Bosch BCI Web*/
    BciComponentModule,
    BciLayoutModule,
    BciSharedModule,
    PaginatorComponent,
    CreatePartDerivationModalComponent,
    TranslationNamePipe,
    CountryDatePipe,
    DownloadDataApiExcelComponent,
    FormsModule,
    ReactiveFormsModule,
    AngularMaterialModule,
    TranslateModule,
    RescheduleModalModule,
    ModalApprovalModule,
    DownloadUploadFileModule,
    NgxMaskDirective,
    ConfirmModalComponent,
    WarningModalComponent,
    UploadTemplateModalComponent,
    UploadErrorsTemplateModalComponent,
    ...components,
    ...pipes,
    ...directives
  ],
  providers: [
    provideNgxMask(maskConfig),
    MasterDetailViewComponent,
    AddCountryCodePipe,
    CountryDatePipe,
    CurrencyPipe
  ]
})
export class SharedModule {}
